import type { ChangeEvent, FormEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { sendResetPasswordEmail } from 'api/auth'
import TextInput from 'components/Common/Form/Input/TextInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'

interface Props {
  dismissable?: boolean
}

function AccountAccessExpiredResetPassword({ dismissable }: Props) {
  const intl = useIntl()
  const [email, setEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [emailError, setEmailError] = useState<string>()
  const emailInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    emailInput.current?.focus()
  }, [])

  const expiredTitle = intl.formatMessage({
    id: '3HoZT9I4iCEg7ooUsAG2',
    defaultMessage: 'This password link has expired',
    description: 'Title shown when a password reset link is expired, directing the customer to request a replacement link',
  })

  const expiredDescription = intl.formatMessage({
    id: 'd8asHMo2TR5ZY5b2VOI2',
    defaultMessage: 'Enter your email address and we’ll send you a new link to create your password.',
    description: 'Instructions for requesting a fresh password reset link after an expired link',
  })

  const emailLabel = intl.formatMessage({
    id: 'YCOXr4_9WXxgSYVLJEEI',
    defaultMessage: 'Email address',
    description: 'Email address field label on the expired password reset link recovery form',
  })

  const emailRequired = intl.formatMessage({
    id: 'MqSPN4c2EcnzldVm74g5',
    defaultMessage: 'Email address is required',
    description: 'Validation message when the email field is empty on the expired password reset link recovery form',
  })

  const sendNewLinkLabel = intl.formatMessage({
    id: 'fRHrxRwL5RnuH_iGwYnf',
    defaultMessage: 'Send new link',
    description: 'Button label to request a replacement password reset link after expiry',
  })

  const emailSentTitle = intl.formatMessage({
    id: 'YOOnqRabzLKAwB4XFYNw',
    defaultMessage: 'Check your email',
    description: 'Title shown after requesting a replacement password reset link',
  })

  const emailSentDescription = intl.formatMessage({
    id: 'uuc08OYbzbDOO_VKThhU',
    defaultMessage: 'If an account exists for this email address, we’ve sent a new password link. It will expire in 24 hours.',
    description: 'Generic confirmation after a replacement password reset link is requested',
  })

  const emailSendError = intl.formatMessage({
    id: 'ezMYwpTe8qmomnhltuzV',
    defaultMessage: 'We couldn’t send a new link. Please try again.',
    description: 'Error shown when requesting a replacement password reset link fails',
  })

  const onRequestNewLink = useCallback<FormEventHandler<HTMLFormElement>>(async(event) => {
    event.preventDefault()
    setEmailError(undefined)
    setSendingEmail(true)
    try {
      await sendResetPasswordEmail(email)
      setEmailSent(true)
    } catch {
      setEmailError(emailSendError)
    } finally {
      setSendingEmail(false)
    }
  }, [email, emailSendError])

  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.currentTarget.value)
  }, [])

  if (emailSent) {
    return <>
      <ModalHeader title={emailSentTitle} dismissible={dismissable} />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large">{emailSentDescription}</BodyText>
        </ModalContent>
      </ModalBody>
    </>
  }

  return <form onSubmit={onRequestNewLink} name="expiredResetPasswordForm">
    <ModalHeader title={expiredTitle} dismissible={dismissable} />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24}>
          {emailError && <MessageBanner kind="critical">{emailError}</MessageBanner>}
          <BodyText variant="large">{expiredDescription}</BodyText>
          <TextInput
            id="email"
            type="email"
            name="email"
            ref={emailInput}
            label={emailLabel}
            autoComplete="email"
            requiredErrorMessage={emailRequired}
            required
            value={email}
            onChange={onEmailChange}
          />
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: sendNewLinkLabel,
        type: 'submit',
        disabled: sendingEmail,
      }}
    />
  </form>
}

export default AccountAccessExpiredResetPassword
